/* eslint-disable @typescript-eslint/no-empty-function */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import Notification, { NotificationOptions } from './Notification';

interface NotificationProps {
  showNotification: (notification: NotificationOptions) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationProps>({
  showNotification: () => {},
  hideNotification: () => {}
});

const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<NotificationOptions | null>(null);

  const showNotification = useCallback((notification: NotificationOptions) => {
    setToast(notification);
  }, []);

  const hideNotification = useCallback(() => {
    setToast(null);
  }, []);

  const providerValue = useMemo(
    () => ({ showNotification, hideNotification }),
    [showNotification, hideNotification]
  );

  useEffect(() => {
    if (toast) {
      const timeout = setTimeout(() => {
        hideNotification();
      }, toast.hideTimeout || 5000);
      return () => clearTimeout(timeout);
    }
  }, [toast, hideNotification]);

  return (
    <NotificationContext.Provider value={providerValue}>
      {children}
      {toast && <Notification {...toast} />}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);

NotificationProvider.displayName = 'ToastWrapper';

export default NotificationProvider;
